import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, CalendarDays, CalendarRange, Calendar, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { usePdfExport } from '@/hooks/usePdfExport';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getWeekRange(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
  const monday = new Date(d.setDate(diff));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    start: monday.toISOString().split('T')[0],
    end: sunday.toISOString().split('T')[0]
  };
}

function getMonthRange(monthStr) {
  const [year, mon] = monthStr.split('-');
  const daysInMonth = new Date(parseInt(year), parseInt(mon), 0).getDate();
  return {
    start: `${monthStr}-01`,
    end: `${monthStr}-${String(daysInMonth).padStart(2, '0')}`
  };
}

const VIEW_MODES = [
  { key: 'daily', label: 'Daily', icon: CalendarDays },
  { key: 'weekly', label: 'Weekly', icon: CalendarRange },
  { key: 'monthly', label: 'Monthly', icon: Calendar },
];

export default function DispatchPlan() {
  const [plans, setPlans] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState('daily');
  const userObj = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = (entry) => userObj.role === 'admin' || entry.created_by === userObj.username;
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterWeek, setFilterWeek] = useState(new Date().toISOString().split('T')[0]);
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [formData, setFormData] = useState({ date: new Date().toISOString().split('T')[0], part_id: '', customer_id: '', planned_qty: '' });

  const headers = () => ({});

  const getDateRange = useCallback(() => {
    if (viewMode === 'daily') return { start: filterDate, end: filterDate };
    if (viewMode === 'weekly') return getWeekRange(filterWeek);
    return getMonthRange(filterMonth);
  }, [viewMode, filterDate, filterWeek, filterMonth]);

  const { exporting, handleExport } = usePdfExport('/export/dispatch-plan-pdf', 'Dispatch_Plan');

  const getExportParams = () => {
    const { start, end } = getDateRange();
    return { start_date: start, end_date: end };
  };

  const fetchPlans = useCallback(async () => {
    try {
      let url = `${API}/dispatch-plan`;
      if (viewMode === 'daily') url += `?date=${filterDate}`;
      else if (viewMode === 'weekly') {
        const { start, end } = getWeekRange(filterWeek);
        url += `?start_date=${start}&end_date=${end}`;
      } else {
        const { start, end } = getMonthRange(filterMonth);
        url += `?start_date=${start}&end_date=${end}`;
      }
      const res = await axios.get(url, {});
      setPlans(res.data);
    } catch (err) { console.error(err); toast.error('Failed to load dispatch plans'); }
  }, [viewMode, filterDate, filterWeek, filterMonth]);

  const fetchItems = useCallback(async () => {
    try { const res = await axios.get(`${API}/masters/items`, {}); setItems(res.data); } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);
  const fetchCustomers = useCallback(async () => {
    try { const res = await axios.get(`${API}/masters/customers`, {}); setCustomers(res.data); } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);
  useEffect(() => { fetchItems(); fetchCustomers(); }, [fetchItems, fetchCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.post(`${API}/dispatch-plan`, { ...formData, planned_qty: parseInt(formData.planned_qty), created_by: user.username }, { headers: headers() });
      toast.success('Dispatch plan added');
      setShowForm(false);
      setFormData({ date: filterDate, part_id: '', customer_id: '', planned_qty: '' });
      fetchPlans();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add plan'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dispatch plan entry?')) return;
    try {
      await axios.delete(`${API}/dispatch-plan/${id}`, { headers: headers() });
      toast.success('Plan deleted');
      fetchPlans();
    } catch (err) { console.error(err); toast.error('Failed to delete plan'); }
  };

  const getItemName = (id) => items.find(i => i.part_id === id)?.part_name || '';
  const getCustomerName = (id) => customers.find(c => c.customer_id === id)?.customer_name || id;

  // Group plans by date for weekly/monthly views
  const groupedPlans = useMemo(() => {
    if (viewMode === 'daily') return null;
    const groups = {};
    plans.forEach(p => {
      if (!groups[p.date]) groups[p.date] = [];
      groups[p.date].push(p);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [plans, viewMode]);

  const totalQty = plans.reduce((s, p) => s + p.planned_qty, 0);
  const uniqueParts = [...new Set(plans.map(p => p.part_id))].length;

  const getDateLabel = () => {
    if (viewMode === 'daily') return new Date(filterDate + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    if (viewMode === 'weekly') {
      const { start, end } = getWeekRange(filterWeek);
      return `${new Date(start + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — ${new Date(end + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return new Date(filterMonth + '-01T00:00:00').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const PlanTable = ({ planRows }) => (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b">
          {viewMode === 'daily' ? null : <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>}
          <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
          <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part Name</th>
          <th className="h-10 px-4 text-left font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Customer</th>
          <th className="h-10 px-4 text-right font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Planned Qty</th>
          <th className="h-10 px-4 text-center font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider w-16"></th>
        </tr>
      </thead>
      <tbody>
        {planRows.map((plan, idx) => (
          <tr key={idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`plan-row-${idx}`}>
            {viewMode === 'daily' ? null : <td className="p-4 align-middle font-mono text-xs">{plan.date}</td>}
            <td className="p-4 align-middle font-mono text-xs font-semibold">{plan.part_id}</td>
            <td className="p-4 align-middle text-xs text-text-muted">{getItemName(plan.part_id)}</td>
            <td className="p-4 align-middle text-xs">{getCustomerName(plan.customer_id)}</td>
            <td className="p-4 align-middle text-right font-mono text-xs font-semibold">{plan.planned_qty.toLocaleString()}</td>
            <td className="p-4 align-middle text-center">
              {canModify(plan) && (
              <button data-testid={`delete-plan-${idx}`} onClick={() => handleDelete(plan.id)} className="p-1.5 hover:bg-error/10 hover:text-error transition-all rounded-sm">
                <Trash2 className="w-4 h-4" />
              </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="space-y-6" data-testid="dispatch-plan-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="dispatch-plan-title">DISPATCH PLAN</h1>
          <p className="text-sm text-text-muted mt-2">Plan and track dispatch targets</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="export-plan-pdf-button" variant="outline" onClick={() => handleExport(getExportParams())} disabled={exporting} className="rounded-sm font-medium">
            <FileDown className="w-4 h-4 mr-2" />{exporting ? 'EXPORTING...' : 'EXPORT PDF'}
          </Button>
          <Button data-testid="toggle-plan-form-button" onClick={() => { setShowForm(!showForm); setFormData({ ...formData, date: filterDate }); }} className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium">
            <Plus className="w-4 h-4 mr-2" />{showForm ? 'CANCEL' : 'ADD PLAN'}
          </Button>
        </div>
      </div>

      {/* View Mode Toggle + Filters */}
      <div className="bg-white border border-border rounded-sm p-4">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">View</label>
            <div className="flex border border-border rounded-sm overflow-hidden">
              {VIEW_MODES.map(vm => (
                <button
                  key={vm.key}
                  data-testid={`view-${vm.key}`}
                  onClick={() => setViewMode(vm.key)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium uppercase tracking-wider transition-colors ${viewMode === vm.key ? 'bg-primary text-primary-foreground' : 'bg-white text-text-muted hover:bg-gray-50'}`}
                >
                  <vm.icon className="w-3.5 h-3.5" />{vm.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted mb-2 block">
              {viewMode === 'daily' ? 'Date' : viewMode === 'weekly' ? 'Week of' : 'Month'}
            </label>
            {viewMode === 'daily' && (
              <Input data-testid="filter-date-input" type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="rounded-sm w-48" />
            )}
            {viewMode === 'weekly' && (
              <Input data-testid="filter-week-input" type="date" value={filterWeek} onChange={(e) => setFilterWeek(e.target.value)} className="rounded-sm w-48" />
            )}
            {viewMode === 'monthly' && (
              <Input data-testid="filter-month-input" type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="rounded-sm w-48" />
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {plans.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white border border-border rounded-sm p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Total Entries</p>
            <p className="text-2xl font-bold font-mono mt-1">{plans.length}</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Total Planned Qty</p>
            <p className="text-2xl font-bold font-mono mt-1 text-accent">{totalQty.toLocaleString()}</p>
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-text-muted">Unique Parts</p>
            <p className="text-2xl font-bold font-mono mt-1">{uniqueParts}</p>
          </div>
        </div>
      )}

      {/* Add Form */}
      {showForm && (
        <div className="bg-white border border-border shadow-sm p-6">
          <h2 className="text-2xl font-bold uppercase mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>ADD DISPATCH PLAN</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
              <Input data-testid="plan-date-input" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="mt-1.5 rounded-none" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part</Label>
              <SearchableSelect
                testId="plan-part-select"
                className="mt-1.5"
                options={items.map(i => ({ value: i.part_id, label: `${i.part_id} — ${i.part_name}`, searchStr: `${i.part_id} ${i.part_name}` }))}
                value={formData.part_id}
                onValueChange={(v) => setFormData({ ...formData, part_id: v })}
                placeholder="Search part..."
                searchPlaceholder="Type to search parts..."
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Customer</Label>
              <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                <SelectTrigger data-testid="plan-customer-select" className="mt-1.5 rounded-none"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.customer_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Planned Qty</Label>
              <Input data-testid="plan-qty-input" type="number" value={formData.planned_qty} onChange={(e) => setFormData({ ...formData, planned_qty: e.target.value })} required className="mt-1.5 rounded-none" />
            </div>
            <div className="col-span-4 flex justify-end pt-4">
              <Button data-testid="submit-plan-button" type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">ADD PLAN</Button>
            </div>
          </form>
        </div>
      )}

      {/* Plan Table */}
      <div className="bg-white border border-border shadow-sm">
        <div className="border-b border-border p-4 bg-gray-50/50">
          <h2 className="text-xl font-semibold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            DISPATCH PLAN — {getDateLabel()}
          </h2>
        </div>
        {plans.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No dispatch plans for this period</div>
        ) : viewMode === 'daily' ? (
          <div className="overflow-x-auto"><PlanTable planRows={plans} /></div>
        ) : (
          <div>
            {groupedPlans.map(([date, datePlans]) => {
              const dayTotal = datePlans.reduce((s, p) => s + p.planned_qty, 0);
              return (
                <div key={date} className="border-b last:border-0">
                  <div className="flex items-center justify-between px-4 py-2 bg-gray-50/80 border-b">
                    <span className="font-mono text-xs font-semibold">{new Date(date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <span className="font-mono text-xs font-semibold text-accent">{dayTotal.toLocaleString()} pcs</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <tbody>
                        {datePlans.map((plan, idx) => (
                          <tr key={idx} className="border-b last:border-0 transition-colors hover:bg-muted/50">
                            <td className="p-3 pl-4 align-middle font-mono text-xs font-semibold">{plan.part_id}</td>
                            <td className="p-3 align-middle text-xs text-text-muted">{getItemName(plan.part_id)}</td>
                            <td className="p-3 align-middle text-xs">{getCustomerName(plan.customer_id)}</td>
                            <td className="p-3 align-middle text-right font-mono text-xs font-semibold">{plan.planned_qty.toLocaleString()}</td>
                            <td className="p-3 pr-4 align-middle text-center w-16">
                              {canModify(plan) && (
                              <button onClick={() => handleDelete(plan.id)} className="p-1.5 hover:bg-error/10 hover:text-error transition-all rounded-sm">
                                <Trash2 className="w-4 h-4" />
                              </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
